import { rem } from '@mantine/core';
import { useHotkeys, useLocalStorage } from '@mantine/hooks';
import type { QueryClient } from '@tanstack/react-query';
import { createRootRouteWithContext, useRouterState } from '@tanstack/react-router';
import { lazy, useCallback } from 'react';
import { useScan } from 'react-scan';
import z from 'zod';

import { AppleLayout } from '@apple/ui/layouts/AppleLayout';
import { MaintenancePage } from '@apple/ui/shell/pages/MaintenancePage';
import { getEnv } from '@apple/utils/config/env';

import type { AuthContext } from '@apple/features/auth-retail/contexts/auth-context';
import { CartMenu } from '@apple/features/cart-retail/components/CartMenu';
import type { CurrentUserLanguage } from '@apple/features/user/hooks/useCurrentUserLanguage';

import { useAppleRetailNavMenu } from '../hooks/useAppleRetailNavMenu';

export type RootRouteContext = {
	queryClient: QueryClient;
	auth: AuthContext;
	lang: CurrentUserLanguage;
};

const rootSearchParamsSchema = z.object({
	hideHeaderAndSidebar: z.boolean().optional(),
});

export const Route = createRootRouteWithContext<RootRouteContext>()({
	wrapInSuspense: true,
	validateSearch: rootSearchParamsSchema,
	component: RootRoute,
});

const env = getEnv();
const devtoolsEnabled = env.DEV && env.APPLE_DISABLE_DEV_TOOLS !== true;

function RootRoute() {
	const { auth } = Route.useRouteContext();
	const [devtoolsVisible, setDevtoolsVisible] = useLocalStorage<boolean>({
		key: 'devtools-visible',
		defaultValue: devtoolsEnabled,
		getInitialValueInEffect: false,
	});
	useHotkeys([['alt+x', () => devtoolsEnabled && setDevtoolsVisible(!devtoolsVisible)]]);
	useScan({
		enabled: devtoolsEnabled,
		showToolbar: devtoolsVisible,
	});

	return (
		<>
			{auth.error ? <MaintenancePage /> : <Layout />}
			{devtoolsVisible && (
				<>
					<TanStackRouterDevtools />
					<ReactQueryDevtools />
				</>
			)}
		</>
	);
}

function Layout() {
	const { menuItems } = useAppleRetailNavMenu();
	const { hideHeaderAndSidebar } = Route.useSearch();
	const navigate = Route.useNavigate();

	const goToCart = useCallback(() => {
		void navigate({ to: '/cart' });
	}, [navigate]);
	const goToCartLastStep = useCallback(() => {
		void navigate({ to: '/cart/submit' });
	}, [navigate]);

	const isLoading = useRouterState({ select: s => s.status === 'pending' });
	return (
		<AppleLayout
			menuItems={menuItems}
			loading={isLoading}
			cartMenu={<CartMenu onCartCleared={goToCart} onViewCartClicked={goToCartLastStep} />}
			logoLabelSize={rem(19)}
			fixedHeader
			hideHeaderAndSidebar={hideHeaderAndSidebar}
		/>
	);
}

const TanStackRouterDevtools = import.meta.env.PROD
	? () => null // Render nothing in production
	: lazy(() =>
			// Lazy load in development
			import('@tanstack/react-router-devtools').then(res => ({
				default: res.TanStackRouterDevtools,
			})),
		);

const ReactQueryDevtools = import.meta.env.PROD
	? () => null // Render nothing in production
	: lazy(() =>
			// Lazy load in development
			import('@tanstack/react-query-devtools').then(res => ({
				default: res.ReactQueryDevtools,
			})),
		);
